import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  forwardRef,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IconComponent } from '../icon/icon.component';
import {
  FIELD_BORDER,
  FIELD_BORDER_ERROR,
  FIELD_ERROR,
  FIELD_HINT,
  FIELD_INPUT,
  FIELD_LABEL,
} from './field-styles';
import { uniqueId } from './ids';

/**
 * Campo password con mostra/nascondi. Con `minLength` indica la lunghezza minima nel testo
 * d'aiuto e quanti caratteri mancano, senza bloccare la digitazione.
 */
@Component({
  selector: 'bs-password-field',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PasswordFieldComponent),
      multi: true,
    },
  ],
  host: { class: 'flex flex-col gap-1.5' },
  template: `
    <label [for]="id" [class]="labelClass">
      {{ label() }}
      @if (required()) {
        <span class="text-stone-500 dark:text-stone-400" aria-hidden="true">*</span>
      }
    </label>
    <div class="relative">
      <input
        #control
        [id]="id"
        [type]="visible() ? 'text' : 'password'"
        [class]="inputClass()"
        [value]="value()"
        [disabled]="disabled()"
        [required]="required()"
        [attr.name]="name()"
        [attr.autocomplete]="autocomplete()"
        [attr.aria-invalid]="error() ? 'true' : null"
        [attr.aria-describedby]="describedBy()"
        [attr.data-testid]="testId()"
        spellcheck="false"
        autocapitalize="off"
        (input)="onInput($event)"
        (blur)="onTouched()"
      />
      <button
        type="button"
        class="absolute inset-y-0 right-0 inline-flex w-11 items-center justify-center rounded-r-xl text-stone-600 hover:text-ink dark:text-stone-300 dark:hover:text-stone-50"
        [attr.aria-label]="visible() ? 'Nascondi password' : 'Mostra password'"
        [attr.aria-pressed]="visible()"
        [attr.aria-controls]="id"
        [disabled]="disabled()"
        [attr.data-testid]="testId() ? testId() + '-toggle' : null"
        (click)="visible.set(!visible())"
      >
        <bs-icon [name]="visible() ? 'eye-off' : 'eye'" [size]="20" />
      </button>
    </div>
    @if (hintText(); as text) {
      <p [id]="hintId" [class]="hintClass">{{ text }}</p>
    }
    @if (error(); as message) {
      <p
        [id]="errorId"
        [class]="errorClass"
        [attr.data-testid]="testId() ? testId() + '-error' : null"
      >
        {{ message }}
      </p>
    }
  `,
})
export class PasswordFieldComponent implements ControlValueAccessor {
  readonly label = input.required<string>();
  readonly hint = input<string | null>(null);
  readonly error = input<string | null>(null);
  readonly minLength = input<number | null>(null);
  readonly required = input(false);
  readonly name = input<string | null>(null);
  readonly autocomplete = input<'current-password' | 'new-password'>('current-password');
  readonly testId = input<string | null>(null);

  protected readonly id = uniqueId('bs-password');
  protected readonly hintId = `${this.id}-hint`;
  protected readonly errorId = `${this.id}-error`;
  protected readonly labelClass = FIELD_LABEL;
  protected readonly hintClass = FIELD_HINT;
  protected readonly errorClass = FIELD_ERROR;

  protected readonly value = signal('');
  protected readonly disabled = signal(false);
  protected readonly visible = signal(false);
  private readonly control = viewChild.required<ElementRef<HTMLInputElement>>('control');

  protected readonly hintText = computed(() => {
    const min = this.minLength();
    if (this.hint()) {
      return this.hint();
    }
    if (!min) {
      return null;
    }
    const missing = min - this.value().length;
    return missing > 0
      ? `Almeno ${min} caratteri: ne mancano ${missing}.`
      : `Almeno ${min} caratteri: requisito soddisfatto.`;
  });
  protected readonly inputClass = computed(
    () => `${FIELD_INPUT} pr-11 ${this.error() ? FIELD_BORDER_ERROR : FIELD_BORDER}`,
  );
  protected readonly describedBy = computed(() => {
    const ids = [this.hintText() ? this.hintId : null, this.error() ? this.errorId : null];
    return ids.filter(Boolean).join(' ') || null;
  });

  private onChange: (value: string) => void = () => undefined;
  protected onTouched: () => void = () => undefined;

  writeValue(value: unknown): void {
    this.value.set(
      typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '',
    );
  }
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(disabled: boolean): void {
    this.disabled.set(disabled);
  }

  focus(): void {
    this.control().nativeElement.focus();
  }

  protected onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.value.set(value);
    this.onChange(value);
  }
}
